import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { useSessionSocket, useCountdown } from "../../lib/ws";
import { AvatarBuilder, Avatar, randomAvatarConfig } from "../../components/AvatarBuilder";
import { MetricsGrid, METRIC_META } from "../../components/MetricsViz";
import { ChallengeRenderer } from "../../components/ChallengeRenderer";
import { AwardCard } from "../../components/Awards";
import { toast } from "sonner";

export default function PlayArea() {
  const { participantId } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [localStage, setLocalStage] = useState("nickname"); // nickname | avatar | ready | challenge | report
  const [avatarCfg, setAvatarCfg] = useState(() => randomAvatarConfig());
  const [lastSubmittedChallenge, setLastSubmittedChallenge] = useState(null);
  const [deltas, setDeltas] = useState({});
  const [completedTopic, setCompletedTopic] = useState(null);
  const [report, setReport] = useState(null);
  const [classRank, setClassRank] = useState(null);
  const [resultModal, setResultModal] = useState(null);

  const loadClassRank = useCallback(async () => {
    try {
      const { data } = await api.get(`/participants/${participantId}/class-rank`);
      setClassRank(data);
    } catch (e) {
      // non-fatal — student just won't see ranking chips
    }
  }, [participantId]);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get(`/participants/${participantId}`);
      setState(data);
      if (data.participant.avatar_config && localStage === "nickname") setLocalStage("avatar");
      if (data.participant.stage === "ready" && localStage !== "challenge" && localStage !== "report") {
        setLocalStage("ready");
      }
      // Only auto-jump to challenge if user hasn't already answered it
      if (
        data.session?.current_challenge &&
        data.session?.current_challenge_status === "running" &&
        !data.progress?.has_answered_current
      ) {
        setLocalStage("challenge");
      } else if (data.progress?.has_answered_current && localStage === "challenge") {
        // User already answered → push back to lobby
        setLocalStage("ready");
      }
      if (data.session?.status === "finished") {
        loadReport();
      }
    } catch (e) {
      if (e.response?.status === 404) {
        localStorage.removeItem("dm_participant_id");
        navigate("/");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [participantId, localStage]);

  const loadReport = async () => {
    try {
      const [{ data }, { data: aw }] = await Promise.all([
        api.get(`/participants/${participantId}/report`),
        api.get(`/participants/${participantId}/awards`),
      ]);
      setReport({ ...data, my_awards: aw.awards || [] });
      setLocalStage("report");
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line
  }, [participantId]);

  // Refresh class rank whenever we enter the lobby (cheap, anonymous)
  useEffect(() => {
    if (localStage === "ready") loadClassRank();
  }, [localStage, loadClassRank]);

  useSessionSocket(state?.session?.id, (msg) => {
    if (msg.type === "challenge_started" || msg.type === "snapshot" || msg.type === "challenge_control") {
      refresh();
      if (msg.type === "challenge_started") {
        setLastSubmittedChallenge(null);
        setCompletedTopic(null); // new challenge — clear the topic recap
        setResultModal(null); // close the result modal if teacher moved on
      }
    }
    if (msg.type === "session_finished") loadReport();
  });

  const regenerateNickname = async () => {
    const { data } = await api.post(`/participants/${participantId}/nickname/regen`);
    setState((s) => ({ ...s, participant: { ...s.participant, nickname: data.nickname } }));
  };

  const saveAvatar = async () => {
    await api.post(`/participants/${participantId}/avatar`, { participant_id: participantId, avatar_config: avatarCfg });
    await refresh();
    setLocalStage("ready");
    toast.success("Postava uložena!");
  };

  const submitResponse = async (payload) => {
    const ch = state.session.current_challenge;
    const choice = (ch.choices || []).find((c) => c.id === payload?.choice_id);
    try {
      const { data } = await api.post(`/participants/${participantId}/respond`, {
        participant_id: participantId,
        challenge_id: ch.id,
        payload,
      });
      setLastSubmittedChallenge(ch.id);
      setDeltas(data.effects || {});
      setState((s) => ({ ...s, participant: { ...s.participant, metrics: data.metrics } }));
      if (data.completed_topic) {
        setCompletedTopic(data.completed_topic);
      }
      // Re-fetch so progress.has_answered_current flips true → WaitingScreen render condition holds
      await refresh();
      // Show an explanatory result modal (stays until the student taps continue) instead
      // of a quick flash — so they really see how their choice changed the character.
      setResultModal({
        effects: data.effects || {},
        metrics: data.metrics || {},
        choiceLabel: choice?.label || "",
      });
      toast.success("Odpověď odeslaná");
    } catch (e) {
      toast.error("Chyba při odesílání");
    }
  };

  const dismissResult = () => {
    setResultModal(null);
    setDeltas({});
    setLocalStage("ready");
  };

  const participant = state?.participant;
  const session = state?.session;
  const cur = session?.current_challenge;

  const remaining = useCountdown(
    session?.current_challenge_started_at,
    session?.current_challenge_duration,
    session?.current_challenge_status
  );

  if (!state) return <LoadingState />;

  // Flow control
  const submitted = lastSubmittedChallenge === cur?.id;

  return (
    <div className="min-h-screen bg-brand-cream px-4 py-5 sm:px-6 pb-24">
      {/* Header */}
      <div className="max-w-xl mx-auto flex items-center justify-between mb-4">
        <div className="brut-chip text-xs">
          <div className="w-2 h-2 bg-brand-mint rounded-full animate-pulse" />
          {session?.code}
        </div>
        <div className="text-xs font-bold text-black/50 truncate max-w-[200px]">
          {session?.title}
        </div>
      </div>

      <div className="max-w-xl mx-auto">
        {localStage === "nickname" && !participant.avatar_config && (
          <NicknameScreen
            nickname={participant.nickname}
            avatarCfg={avatarCfg}
            onRegenerate={regenerateNickname}
            onContinue={() => setLocalStage("avatar")}
          />
        )}
        {localStage === "avatar" && !participant.avatar_config && (
          <AvatarScreen avatarCfg={avatarCfg} setAvatarCfg={setAvatarCfg} onSave={saveAvatar} />
        )}
        {localStage === "ready" && (!cur || session?.current_challenge_status !== "running" || state?.progress?.has_answered_current) && (
          <WaitingScreen
            participant={participant}
            avatarCfg={participant.avatar_config}
            progress={state?.progress}
            justAnswered={state?.progress?.has_answered_current}
            deltas={deltas}
            classRank={classRank}
            completedTopic={completedTopic}
            onDismissTopic={() => setCompletedTopic(null)}
          />
        )}
        {localStage === "challenge" && cur && session?.current_challenge_status === "running" && (
          <ChallengeScreen
            challenge={cur}
            topicContext={session.current_topic_context}
            remaining={remaining}
            onSubmit={submitResponse}
            submitted={submitted}
            metrics={participant.metrics}
            deltas={deltas}
            progress={state?.progress}
          />
        )}
        {localStage === "report" && report && <ReportScreen report={report} />}
      </div>

      {resultModal && (
        <ResultModal result={resultModal} onContinue={dismissResult} />
      )}
    </div>
  );
}

function LoadingState() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-cream">
      <div className="brut-card p-8 animate-pulse font-bold">Načítám...</div>
    </div>
  );
}

function NicknameScreen({ nickname, avatarCfg, onRegenerate, onContinue }) {
  return (
    <div className="space-y-5 animate-fade-up">
      <div className="text-xs font-black uppercase tracking-widest text-black/60">Krok 1 ze 3</div>
      <h1 className="text-4xl font-heading font-black tracking-tighter leading-[0.95]">
        Tvoje <span className="bg-brand-cyan border-2 border-black px-2 -rotate-1 inline-block">přezdívka</span>
      </h1>
      <div className="brut-card p-6 flex flex-col items-center gap-4">
        <Avatar config={avatarCfg} size={120} className="rounded-xl border-2 border-black" />
        <div className="text-2xl font-black font-heading tracking-tight" data-testid="student-nickname">
          {nickname}
        </div>
        <button
          onClick={onRegenerate}
          className="brut-btn bg-white text-black"
          data-testid="btn-regen-nickname"
        >
          🎲 Zkusit jinou
        </button>
      </div>
      <button
        onClick={onContinue}
        className="brut-btn bg-brand-orange text-white w-full text-lg"
        data-testid="btn-continue-avatar"
      >
        Jedu si vytvořit postavu →
      </button>
    </div>
  );
}

function AvatarScreen({ avatarCfg, setAvatarCfg, onSave }) {
  return (
    <div className="space-y-5 animate-fade-up">
      <div className="text-xs font-black uppercase tracking-widest text-black/60">Krok 2 ze 3</div>
      <h1 className="text-4xl font-heading font-black tracking-tighter leading-[0.95]">
        Postav si <span className="bg-brand-yellow border-2 border-black px-2 rotate-1 inline-block">svého</span> charakera
      </h1>
      <AvatarBuilder value={avatarCfg} onChange={setAvatarCfg} />
      <button onClick={onSave} className="brut-btn bg-brand-orange text-white w-full text-lg" data-testid="btn-save-avatar">
        Takhle vypadám ✓
      </button>
    </div>
  );
}

const METRIC_GUIDE = {
  mood: {
    label: "Nálada",
    color: "#FDE047",
    desc: "Jak se tvoje postava cítí.",
    up: "podpora kamarádů, pozitivní rozhodnutí, pauzy od telefonu",
    down: "stres, drama, samota, ignorování problému",
  },
  aura: {
    label: "Aura",
    color: "#22D3EE",
    desc: "Jak tě ostatní vnímají online.",
    up: "důstojné chování, pomáhání, autenticita",
    down: "drama v komentářích, sdílení toho, čeho budeš litovat",
  },
  trust: {
    label: "Důvěra",
    color: "#1E3A8A",
    desc: "Síla tvých vztahů a podpůrné sítě.",
    up: "otevřenost, naslouchání, opora kamarádům",
    down: "lhaní, zrazování, ignorace ostatních",
  },
  safety: {
    label: "Bezpečí",
    color: "#10B981",
    desc: "Jak dobře tě chrání tvoje hranice.",
    up: "zdravé hranice, hlášení problémů, odmítnutí rizika",
    down: "sdílení osobních věcí, kontakt s neznámými, ignorace red flagů",
  },
  stress: {
    label: "Stres",
    color: "#F43F5E",
    desc: "Vnitřní napětí. Méně = líp.",
    up: "tlak sítí, FOMO, konflikty, špatná rozhodnutí",
    down: "pauzy, podpora, jasný plán, říct si o pomoc",
  },
};

function ResultModal({ result, onContinue }) {
  const { effects = {}, metrics = {}, choiceLabel } = result;
  const changed = Object.entries(effects).filter(([, v]) => v !== 0);
  const anyChange = changed.length > 0;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-3 sm:p-6 animate-fade-up"
      data-testid="result-modal"
    >
      <div className="brut-card bg-brand-cream w-full max-w-md max-h-[92vh] overflow-y-auto p-5 sm:p-6">
        <div className="text-[10px] font-black uppercase tracking-widest text-black/60 mb-1">
          ✓ Odpověď uložena
        </div>
        <h2 className="text-3xl font-heading font-black tracking-tighter leading-[0.95] mb-1">
          Co tvoje volba <span className="bg-brand-cyan border-2 border-black px-2 -rotate-1 inline-block">udělala</span>
        </h2>
        {choiceLabel && (
          <div className="text-sm font-bold text-black/70 mb-4" data-testid="result-choice">
            Vybral/a jsi: „{choiceLabel}"
          </div>
        )}

        {anyChange ? (
          <div className="space-y-2.5 mb-5">
            {changed.map(([k, delta]) => {
              const m = METRIC_GUIDE[k] || { label: k, color: "#999", up: "", down: "" };
              const good = k === "stress" ? delta < 0 : delta > 0;
              const reason = delta > 0 ? m.up : m.down;
              const value = Math.round(metrics[k] ?? 0);
              return (
                <div key={k} className="brut-card p-3" data-testid={`result-metric-${k}`}>
                  <div className="flex items-center gap-3">
                    <div
                      className="w-11 h-11 rounded-full flex items-center justify-center font-black text-sm tabular-nums flex-shrink-0 border-2 border-black"
                      style={{ background: m.color, color: k === "trust" ? "#fff" : "#0a0a0a" }}
                    >
                      {value}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="font-black text-base">{m.label}</div>
                        <span
                          className={`text-xs font-black px-2 py-0.5 rounded-full border-2 border-black ${
                            good ? "bg-brand-mint text-white" : "bg-brand-pink text-white"
                          }`}
                          data-testid={`result-delta-${k}`}
                        >
                          {delta > 0 ? "+" : ""}{delta}
                        </span>
                        <span className="text-lg">{good ? "😊" : "😟"}</span>
                      </div>
                      {reason && (
                        <div className="text-xs text-black/70 leading-snug mt-0.5">
                          {good ? "Pomohlo: " : "Zhoršilo to: "}{reason}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="brut-card p-4 bg-white mb-5 text-sm font-bold">
            Tahle volba nechala tvoji postavu v klidu — nic se výrazně nezměnilo.
          </div>
        )}

        <div className="brut-card p-3 bg-brand-yellow/60 mb-4 text-xs font-bold leading-snug">
          Pamatuj: není tu správná a špatná odpověď. Každé rozhodnutí tvoji postavu jen trochu posune.
        </div>

        <button
          onClick={onContinue}
          className="brut-btn bg-brand-orange text-white w-full text-lg"
          data-testid="btn-result-continue"
        >
          Pokračovat →
        </button>
      </div>
    </div>
  );
}

function WaitingScreen({ participant, avatarCfg, progress, justAnswered, deltas, classRank, completedTopic, onDismissTopic }) {
  const tips = [
    "Tip: Ochrana soukromí začíná u tebe. Sdílej jen to, co by ti nevadilo v novinách.",
    "Tip: Když ti něco na síti nesedí, důvěřuj instinktu. Screenshot + dospělý.",
    "Tip: Nejde o to být neviditelný. Jde o to vědět, kdo tě vidí.",
    "Tip: Pauza od telefonu = super vlastnost, ne slabost.",
    "Tip: Říct ne není nezdvořilé. Je to hranice.",
  ];
  const tip = tips[Math.floor(Math.random() * tips.length)];
  const metrics = participant.metrics || {};
  const totalChallenges = progress?.total_challenges || 0;
  const completed = progress?.completed || 0;
  const pct = totalChallenges ? Math.round((completed / totalChallenges) * 100) : 0;
  const rank = classRank?.rank || {};

  return (
    <div className="space-y-5 animate-fade-up relative">
      {/* Topic completion recap (only right after finishing all questions in a topic) */}
      {completedTopic && (
        <div className="brut-card p-5 bg-brand-cyan/40 relative animate-fade-up" data-testid="topic-recap-card">
          <button
            onClick={onDismissTopic}
            className="absolute top-2 right-3 text-black/60 hover:text-black text-lg"
            aria-label="Zavřít"
            data-testid="btn-dismiss-topic-recap"
          >
            ✕
          </button>
          <div className="text-[10px] font-black uppercase tracking-widest text-black/70">
            🎯 Právě jsi zvládl/a téma
          </div>
          <div className="text-2xl font-black font-heading tracking-tight leading-tight mt-1" data-testid="topic-recap-title">
            {completedTopic.topic_title}
          </div>
          {completedTopic.topic_intro && (
            <div className="text-sm text-black/80 mt-2 leading-snug">{completedTopic.topic_intro}</div>
          )}
          {completedTopic.takeaway && (
            <div className="mt-3 bg-white border-2 border-black rounded-md p-3">
              <div className="text-[10px] font-black uppercase tracking-widest text-black/70 mb-1">Co si odnést</div>
              <div className="text-sm font-bold leading-snug">{completedTopic.takeaway}</div>
            </div>
          )}
          <div className="text-[11px] text-black/60 mt-2">
            {completedTopic.question_count} otázk{completedTopic.question_count === 1 ? "a" : completedTopic.question_count < 5 ? "y" : "ek"} · Diskutuj s třídou, další téma přijde brzy.
          </div>
        </div>
      )}

      {/* Status banner */}
      {justAnswered ? (
        <div className="brut-card p-4 bg-brand-mint text-white" data-testid="just-answered-banner">
          <div className="text-[10px] font-black uppercase tracking-widest mb-1">✓ Odpověď uložena</div>
          <div className="text-sm font-bold">Skvělá práce. Čekáme na ostatní a další výzvu.</div>
        </div>
      ) : (
        <div className="text-xs font-black uppercase tracking-widest text-black/60">
          Lobby · čekej na další výzvu
        </div>
      )}

      <h1 className="text-4xl font-heading font-black tracking-tighter leading-[0.95]">
        {justAnswered ? (
          <>Čekáme <span className="bg-brand-mint border-2 border-black px-2 -rotate-1 inline-block text-white">na další</span>.</>
        ) : (
          <>Jsi v <span className="bg-brand-mint border-2 border-black px-2 -rotate-1 inline-block text-white">lobby</span>. Dobrá práce.</>
        )}
      </h1>

      {/* Progress */}
      {totalChallenges > 0 && (
        <div className="brut-card p-4" data-testid="progress-bar">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-black uppercase tracking-widest">Postup workshopem</div>
            <div className="text-sm font-bold tabular-nums">
              {completed} / {totalChallenges}
            </div>
          </div>
          <div className="h-3 bg-black/10 rounded-full overflow-hidden border-2 border-black">
            <div className="h-full bg-brand-orange transition-all duration-700" style={{ width: `${pct}%` }} />
          </div>
        </div>
      )}

      {/* Avatar card */}
      <div className="brut-card p-5 flex items-center gap-4">
        <div style={{ background: `#${avatarCfg?.bg || "FACC15"}` }} className="rounded-xl overflow-hidden border-2 border-black flex-shrink-0">
          <Avatar config={avatarCfg} size={92} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-widest text-black/50 font-bold">Tvoje postava</div>
          <div className="text-xl font-black font-heading tracking-tight truncate">{participant.nickname}</div>
        </div>
      </div>

      {/* Recovery code — student needs this if they get disconnected */}
      {participant.recovery_code && (
        <div className="brut-card p-4 bg-brand-cyan/30" data-testid="recovery-code-card">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-[10px] font-black uppercase tracking-widest mb-0.5">🔑 Tvůj recovery kód</div>
              <div className="text-xs text-black/70 leading-snug max-w-[14rem]">
                Když ztratíš spojení, zadej tenhle kód v „Vracím se".
              </div>
            </div>
            <div className="font-mono font-black text-2xl tabular-nums tracking-widest bg-white border-2 border-black rounded-lg px-3 py-2">
              {participant.recovery_code}
            </div>
          </div>
        </div>
      )}

      {/* Metrics with explanations */}
      <div>
        <div className="text-xs font-black uppercase tracking-widest mb-3">Stav postavy</div>
        <div className="space-y-2">
          {Object.entries(METRIC_GUIDE).map(([k, m]) => {
            const value = metrics[k] ?? 0;
            const delta = deltas?.[k] || 0;
            const r = rank[k];
            return (
              <div key={k} className="brut-card p-3" data-testid={`lobby-metric-${k}`}>
                <div className="flex items-center gap-3">
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center font-black text-sm tabular-nums flex-shrink-0 border-2 border-black"
                    style={{ background: m.color, color: k === "trust" ? "#fff" : "#0a0a0a" }}
                  >
                    {Math.round(value)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-black text-sm">{m.label}</div>
                      {delta !== 0 && (
                        <span className={`text-[10px] font-black px-1.5 py-0.5 rounded-full border border-black ${
                          (k === "stress" ? delta < 0 : delta > 0) ? "bg-brand-mint text-white" : "bg-brand-pink text-white"
                        }`}>
                          {delta > 0 ? "+" : ""}{delta}
                        </span>
                      )}
                      {r && r.total > 1 && (
                        <span className="text-[10px] font-black px-1.5 py-0.5 rounded-full bg-black text-white" data-testid={`rank-${k}`}>
                          {r.label}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-black/60 leading-tight">{m.desc}</div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 mt-2 text-[11px] leading-snug">
                  <div className="bg-brand-mint/15 border border-black/10 rounded-md p-2">
                    <div className="font-black text-[9px] uppercase tracking-wider text-brand-mint mb-0.5">↑ Roste</div>
                    {m.up}
                  </div>
                  <div className="bg-brand-pink/15 border border-black/10 rounded-md p-2">
                    <div className="font-black text-[9px] uppercase tracking-wider text-brand-pink mb-0.5">↓ Klesá</div>
                    {m.down}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="brut-card p-4 bg-brand-yellow/60">
        <div className="text-[10px] font-black uppercase tracking-widest mb-1">💡 Tip pro tebe</div>
        <div className="text-sm font-bold leading-snug">{tip}</div>
      </div>
    </div>
  );
}

function ChallengeScreen({ challenge, topicContext, remaining, onSubmit, submitted, metrics, deltas, progress }) {
  const total = progress?.total_challenges || 0;
  const completed = progress?.completed || 0;
  return (
    <div className="space-y-4 animate-fade-up">
      {topicContext && (
        <div className="flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-black/60" data-testid="topic-breadcrumb">
          <span className="bg-brand-yellow border-2 border-black px-2 py-0.5 rounded">
            Téma {topicContext.topic_index}/{topicContext.total_topics}
          </span>
          <span className="truncate">{topicContext.topic_title}</span>
          <span className="text-black/40">·</span>
          <span>Otázka {topicContext.question_index}/{topicContext.total_questions}</span>
        </div>
      )}
      {total > 0 && (
        <div className="flex items-center gap-2 text-xs font-black uppercase tracking-widest">
          <span className="text-black/50">Postup:</span>
          <div className="flex-1 h-1.5 bg-black/10 rounded-full overflow-hidden border border-black">
            <div className="h-full bg-brand-orange" style={{ width: `${total ? (completed / total) * 100 : 0}%` }} />
          </div>
          <span className="tabular-nums">{completed}/{total}</span>
        </div>
      )}
      <div className="brut-card p-4">
        <MetricsGrid metrics={metrics} deltas={deltas} compact />
      </div>
      <ChallengeRenderer
        challenge={challenge}
        remaining={remaining}
        onSubmit={onSubmit}
        submitted={submitted}
      />
      {submitted && (
        <div className="brut-card p-4 bg-brand-mint text-white text-center font-bold animate-fade-up">
          ✓ Odpověď uložena, přesouvám tě do lobby...
        </div>
      )}
    </div>
  );
}

function ReportScreen({ report }) {
  return (
    <div className="space-y-5 animate-fade-up">
      <div className="text-xs font-black uppercase tracking-widest text-black/60">Finále</div>
      <h1 className="text-4xl font-heading font-black tracking-tighter leading-[0.95]">
        {report.ending_title}
      </h1>
      <div className="brut-card p-6 flex flex-col items-center gap-3 bg-white">
        <div style={{ background: `#${report.avatar_config?.bg || "FACC15"}` }} className="rounded-xl overflow-hidden border-2 border-black">
          <Avatar config={report.avatar_config} size={200} />
        </div>
        <div className="text-2xl font-black font-heading tracking-tight">{report.nickname}</div>
      </div>

      {/* Your awards */}
      {report.my_awards?.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <span className="text-xl">🏆</span>
            <div className="text-xs font-black uppercase tracking-widest">
              {report.my_awards.length === 1 ? "Vyhrál/a jsi ocenění" : `Vyhrál/a jsi ${report.my_awards.length} ocenění`}
            </div>
          </div>
          <div className="space-y-2">
            {report.my_awards.map((a) => (
              <AwardCard key={a.id} award={a} compact />
            ))}
          </div>
        </div>
      )}

      <div className="brut-card p-4 bg-brand-yellow">
        <div className="text-sm leading-relaxed font-semibold">{report.ai_summary || report.ending_description}</div>
      </div>
      <div>
        <div className="text-xs font-black uppercase tracking-wider mb-2">Stav tvé postavy</div>
        <MetricsGrid metrics={report.metrics} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {Object.entries(METRIC_META).map(([k, m]) => (
          <div key={k} className="brut-card p-3 text-xs">
            <div className="font-black uppercase">{m.label}</div>
            <div className="text-black/60">{m.desc}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
